type PublicationEvent = { portalId: string; at: string; live: boolean };
export type PublicationCoverage = 'complete' | 'partial';
export type PublicationHistory = {
  coverage: PublicationCoverage;
  firstPublishedAt: string | null;
  observedSince: string;
  events: PublicationEvent[];
};

export const isPublicationHistory = (
  value: unknown,
): value is PublicationHistory =>
  typeof value === 'object' &&
  value != null &&
  'coverage' in value &&
  (value.coverage === 'complete' || value.coverage === 'partial') &&
  'firstPublishedAt' in value &&
  (value.firstPublishedAt == null ||
    (typeof value.firstPublishedAt === 'string' &&
      Number.isFinite(Date.parse(value.firstPublishedAt)))) &&
  'observedSince' in value &&
  typeof value.observedSince === 'string' &&
  Number.isFinite(Date.parse(value.observedSince)) &&
  'events' in value &&
  Array.isArray(value.events) &&
  value.events.every(
    (event: unknown) =>
      typeof event === 'object' &&
      event != null &&
      'portalId' in event &&
      typeof event.portalId === 'string' &&
      'at' in event &&
      typeof event.at === 'string' &&
      Number.isFinite(Date.parse(event.at)) &&
      'live' in event &&
      typeof event.live === 'boolean',
  );

export const calculateDaysOnMarket = (
  history: PublicationHistory,
  now = Date.now(),
) => {
  const online = new Set<string>();
  let startedAt: number | null = null;
  let published = false;
  let milliseconds = 0;

  for (const event of [...history.events].sort(
    (a, b) => Date.parse(a.at) - Date.parse(b.at),
  )) {
    const at = Math.min(Date.parse(event.at), now);
    if (!Number.isFinite(at)) {
      continue;
    }
    if (event.live) {
      if (online.size === 0) {
        startedAt = at;
      }
      published = true;
      online.add(event.portalId);
    } else {
      online.delete(event.portalId);
      if (online.size === 0 && startedAt != null) {
        milliseconds += at - startedAt;
        startedAt = null;
      }
    }
  }
  if (startedAt != null) {
    milliseconds += now - startedAt;
  }
  return {
    days: Math.floor(Math.max(0, milliseconds) / 86_400_000),
    online: online.size > 0,
    published,
    coverage: history.coverage,
    observedSince: new Date(history.observedSince),
    since:
      history.firstPublishedAt != null
        ? new Date(history.firstPublishedAt)
        : null,
  };
};
